import { get_token } from "@/router";
import Swal from "sweetalert2";

const SEATING_API_URL = `${process.env.VUE_APP_BACKEND_API_URL}/event_management`;

function request_headers() {
  return {
    Authorization: `Token ${get_token()}`,
    "Content-Type": "application/json",
  };
}

function event_id_for(vm) {
  return Number(vm.event_id || vm.event?.id || vm.$route.params.event_id);
}

function error_message(xhr, fallback) {
  return xhr?.responseJSON?.message || fallback;
}

function notify(icon, title, text) {
  Swal.fire({
    icon,
    title,
    text,
    timer: 2500,
    showConfirmButton: false,
    position: "bottom-end",
    toast: true,
  });
}

function user_name(user) {
  const generated_name = [user?.first_name, user?.last_name].filter(Boolean).join(" ");
  return generated_name || "Unknown guest";
}

function normalize_assignment(assignment) {
  return {
    ...assignment,
    user: assignment.user,
    user_id: assignment.user?.id,
    name: user_name(assignment.user),
    seat_number: Number(assignment.seat_number),
  };
}

function assignment_lookup(assignments, key) {
  return Object.fromEntries(
  assignments.map((assignment) => [
    Reflect.get(assignment, key),
    assignment,
  ])
);
}

function normalize_table(table) {
  const assignments = (table.seat_assignments || []).map(normalize_assignment);
  return {
    ...table,
    name: `Table ${table.table_number}`,
    seats: Number(table.seats_count) || 0,
    seat_assignments: assignments,
    assignmentBySeatNumber: assignment_lookup(assignments, "seat_number"),
    assignmentByUserId: assignment_lookup(assignments, "user_id"),
  };
}

function rsvp_user(entry) {
  return entry?.user || entry;
}

function add_user_to_map(users, user) {
  if (!user?.id) return;
  users.set(String(user.id), { id: user.id, name: user_name(user), user });
}

function collect_event_users(event, tables) {
  const users = new Map();
  (event?.rsvp_list || []).forEach((entry) => {
    add_user_to_map(users, rsvp_user(entry));
  });
  tables.forEach((table) => {
    table.seat_assignments.forEach((assignment) => {
      add_user_to_map(users, assignment.user);
    });
  });
  return users;
}

function find_user_assignment(tables, user_id) {
  for (const table of tables) {
    const assignment = Reflect.get(table.assignmentByUserId, user_id);
    if (assignment) return { table, assignment };
  }
  return null;
}

function build_guest_list(event, tables) {
  return Array.from(collect_event_users(event, tables).values()).map((guest) => {
    const location = find_user_assignment(tables, guest.id);
    return {
      ...guest,
      table_id: location?.table.id ?? null,
      seat_number: location?.assignment.seat_number ?? null,
    };
  });
}

function next_table_number(tables) {
  return tables.reduce((highest, table) => Math.max(highest, Number(table.table_number) || 0), 0) + 1;
}

const manage_event_seating_api = {
  get_event_seating() {
    const event_id = event_id_for(this);
    if (!event_id) return null;
    this.seating_loading = true;
    this.seating_error = "";
    return $.ajax({
      url: `${SEATING_API_URL}/get_event_seating/`,
      type: "POST",
      data: JSON.stringify({ event_id }),
      xhrFields: { withCredentials: true },
      headers: request_headers(),
      success: (response) => this.apply_event_seating(response),
      error: (xhr) => this.handle_seating_error(xhr, "Unable to load event seating."),
      complete: () => (this.seating_loading = false),
    });
  },
  apply_event_seating(response) {
    if (response?.status !== "success" || !Array.isArray(response?.data)) {
      this.handle_seating_error({ responseJSON: response }, "Unable to load event seating.");
      return;
    }
    this.venue_tables = response.data.map(normalize_table);
    this.venue_guests = build_guest_list(this.event, this.venue_tables);
    if (!this.venue_tables.some((table) => table.id === this.selected_venue_table_id)) {
      this.selected_venue_table_id = null;
    }
    if (this.selected_venue_table) {
      this.venue_table_seat_drafts[this.selected_venue_table.id] = this.selected_venue_table.seats;
    }
  },
  handle_seating_error(xhr, fallback) {
    const message = error_message(xhr, fallback);
    this.seating_error = message;
    notify("error", "Unable to update seating", message);
  },
  create_venue_table() {
    const event_id = event_id_for(this);
    if (!event_id || this.seating_submitting) return null;
    this.seating_submitting = true;
    this.seating_error = "";
    return $.ajax({
      url: `${SEATING_API_URL}/create_event_table/`,
      type: "POST",
      data: JSON.stringify({
        event_id,
        table_number: next_table_number(this.venue_tables),
        seats_count: 0,
      }),
      xhrFields: { withCredentials: true },
      headers: request_headers(),
      success: (response) => this.handle_create_table_success(response),
      error: (xhr) => this.handle_seating_error(xhr, "Unable to create the table."),
      complete: () => (this.seating_submitting = false),
    });
  },
  handle_create_table_success(response) {
    if (response?.status !== "success") {
      this.handle_seating_error({ responseJSON: response }, "Unable to create the table.");
      return;
    }
    notify("success", "Created!", response.message || "Table created successfully.");
    this.get_event_seating();
  },
  update_venue_table_capacity(table, value) {
    const seats_count = Math.max(0, Math.floor(Number(value) || 0));
    if (this.seating_submitting) return null;
    this.seating_submitting = true;
    this.seating_error = "";
    return $.ajax({
      url: `${SEATING_API_URL}/create_event_table_seat/`,
      type: "POST",
      data: JSON.stringify({ event_table_id: table.id, seats_count }),
      xhrFields: { withCredentials: true },
      headers: request_headers(),
      success: (response) => this.handle_capacity_success(response, table),
      error: (xhr) => this.handle_capacity_error(xhr, table),
      complete: () => (this.seating_submitting = false),
    });
  },
  handle_capacity_success(response, table) {
    if (response?.status !== "success") {
      this.venue_table_seat_drafts[table.id] = table.seats;
      this.handle_seating_error({ responseJSON: response }, "Unable to update the table capacity.");
      return;
    }
    notify("success", "Saved!", response.message || "Table capacity updated successfully.");
    this.get_event_seating();
  },
  handle_capacity_error(xhr, table) {
    this.venue_table_seat_drafts[table.id] = table.seats;
    this.handle_seating_error(xhr, "Unable to update the table capacity.");
  },
  assign_venue_seat(table_id, seat_number) {
    const assignment = this.venue_guest_for_seat(table_id, seat_number);
    if (assignment) return this.unassign_venue_guest(assignment.id);
    if (!this.selected_venue_guest_id || this.seating_submitting) return null;
    this.selected_venue_seat_number = seat_number;
    this.seating_submitting = true;
    this.seating_error = "";
    return $.ajax({
      url: `${SEATING_API_URL}/assign_event_seat/`,
      type: "POST",
      data: JSON.stringify({
        event_id: event_id_for(this),
        event_table_id: table_id,
        user_id: this.selected_venue_guest_id,
        seat_number,
      }),
      xhrFields: { withCredentials: true },
      headers: request_headers(),
      success: (response) => this.handle_assign_seat_success(response),
      error: (xhr) => this.handle_seating_error(xhr, "Unable to assign the selected seat."),
      complete: () => {
        this.seating_submitting = false;
        this.selected_venue_seat_number = null;
      },
    });
  },
  handle_assign_seat_success(response) {
    if (response?.status !== "success") {
      this.handle_seating_error({ responseJSON: response }, "Unable to assign the selected seat.");
      return;
    }
    this.selected_venue_guest_id = null;
    this.selected_venue_seat_number = null;
    notify("success", "Assigned!", response.message || "User assigned successfully.");
    this.get_event_seating();
  },
  unassign_venue_guest(user_id) {
    if (!user_id || this.seating_submitting) return null;
    this.seating_submitting = true;
    this.seating_error = "";
    return $.ajax({
      url: `${SEATING_API_URL}/unassign_event_seat/`,
      type: "POST",
      data: JSON.stringify({ event_id: event_id_for(this), user_id }),
      xhrFields: { withCredentials: true },
      headers: request_headers(),
      success: (response) => this.handle_unassign_seat_success(response),
      error: (xhr) => this.handle_seating_error(xhr, "Unable to unassign the user."),
      complete: () => (this.seating_submitting = false),
    });
  },
  handle_unassign_seat_success(response) {
    if (response?.status !== "success") {
      this.handle_seating_error({ responseJSON: response }, "Unable to unassign the user.");
      return;
    }
    notify("success", "Unassigned!", response.message || "User unassigned successfully.");
    this.get_event_seating();
  },
};

export default manage_event_seating_api;
